import { useEffect, useState } from "react";
import { adminEnquiries, adminReplyEnquiry } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Reply, Check } from "lucide-react";
import { extractErrorMessage } from "../../lib/errors";

const SENDER_DISPLAY = "Lime Driving School <info@limedrivingschool.com>";

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [replyDraft, setReplyDraft] = useState({}); // {enquiry_id: {subject, body, sending}}

  const load = () =>
    adminEnquiries().then(setItems).catch(() => toast.error("Could not load enquiries"));

  useEffect(() => { load(); }, []);

  const toggleExpand = (e) => {
    const id = e.enquiry_id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    // Pre-populate draft if not already present.
    setReplyDraft(prev => {
      if (prev[id]) return prev;
      // Smart Re: prefix — don't double-prefix if already starts with Re:/RE:/re:
      const origSubject = (e.subject || "").trim();
      const hasRe = /^re:\s*/i.test(origSubject);
      const subject = origSubject
        ? (hasRe ? origSubject : `Re: ${origSubject}`)
        : `Re: Your enquiry — Lime Driving School`;
      return {
        ...prev,
        [id]: {
          subject,
          body: `Hi ${e.name?.split(" ")[0] || "there"},\n\nThanks for getting in touch with Lime Driving School. \n\n`,
          sending: false,
        },
      };
    });
  };

  const updateDraft = (id, key, val) => {
    setReplyDraft(prev => ({ ...prev, [id]: { ...prev[id], [key]: val } }));
  };

  const sendReply = async (e) => {
    const id = e.enquiry_id;
    const draft = replyDraft[id] || {};
    const subject = (draft.subject || "").trim();
    const body = (draft.body || "").trim();
    if (!subject) return toast.error("Subject is required");
    if (!body) return toast.error("Reply body is empty");

    try {
      updateDraft(id, "sending", true);
      await adminReplyEnquiry(id, { subject, body });
      toast.success(`Reply sent to ${e.email}`);
      // Reset the draft and reload so the new reply appears in history.
      setReplyDraft(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Send failed"));
    } finally {
      updateDraft(id, "sending", false);
    }
  };

  return (
    <div data-testid="admin-enquiries-page">
      <h1 className="text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Enquiries</h1>
      <p className="text-sm text-[#4B5563]">Website enquiry submissions. Click any card to reply directly to the enquirer.</p>
      <div className="mt-6 space-y-3">
        {items.map(e => {
          const expanded = expandedId === e.enquiry_id;
          const draft = replyDraft[e.enquiry_id] || { subject: "", body: "", sending: false };
          const replies = e.replies || [];
          return (
            <div key={e.enquiry_id} className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid={`enquiry-card-${e.enquiry_id}`}>
              <button
                type="button"
                onClick={() => toggleExpand(e)}
                className="w-full text-left p-5 hover:bg-[#F9FAFB] transition-colors"
                data-testid={`enquiry-toggle-${e.enquiry_id}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{e.name}</span>
                      <span
                        className={`inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full ${
                          e.source === "email"
                            ? "text-[#1E40AF] bg-[#DBEAFE]"
                            : "text-[#374151] bg-[#E5E7EB]"
                        }`}
                        title={e.source === "email" ? "Received via email (info@limedrivingschool.com)" : "Submitted via website contact form"}
                      >
                        {e.source === "email" ? "✉ Email" : "🌐 Web form"}
                      </span>
                      {replies.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#15803D] bg-[#DCFCE7] px-2 py-0.5 rounded-full">
                          <Check className="h-3 w-3" /> Replied ({replies.length})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#4B5563] mt-0.5 truncate">{e.email}{e.phone ? ` · ${e.phone}` : ""}</div>
                    {e.subject && (
                      <div className="text-sm font-medium text-[#0A0F00] mt-1 truncate" data-testid="enquiry-subject">
                        Subject: {e.subject}
                      </div>
                    )}
                    {(e.town || e.postcode) && (
                      <div className="text-xs text-[#4B5563] mt-0.5">
                        {[e.town, e.postcode].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-none">
                    <div className="text-xs text-[#9CA3AF] whitespace-nowrap">{new Date(e.created_at).toLocaleString("en-GB")}</div>
                    {expanded ? <ChevronUp className="h-4 w-4 text-[#9CA3AF]" /> : <ChevronDown className="h-4 w-4 text-[#9CA3AF]" />}
                  </div>
                </div>
                <p className="mt-3 text-sm whitespace-pre-wrap text-[#374151] line-clamp-2">{e.message}</p>
              </button>

              {expanded && (
                <div className="border-t border-black/5 p-5 bg-[#FAFBFB] space-y-5">
                  {/* Original message */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF] mb-2">Original message</div>
                    <p className="text-sm whitespace-pre-wrap text-[#0A0F00] bg-white border border-black/5 rounded-lg p-3">{e.message}</p>
                  </div>

                  {/* Reply form */}
                  <div className="space-y-3" data-testid={`reply-form-${e.enquiry_id}`}>
                    <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">Reply</div>
                    <div>
                      <Label className="text-xs">From</Label>
                      <Input value={SENDER_DISPLAY} readOnly className="bg-[#F3F4F6] text-[#4B5563] cursor-not-allowed" data-testid="reply-from" />
                    </div>
                    <div>
                      <Label className="text-xs">To</Label>
                      <Input value={e.email} readOnly className="bg-[#F3F4F6] text-[#4B5563] cursor-not-allowed" data-testid="reply-to" />
                    </div>
                    <div>
                      <Label className="text-xs">Subject</Label>
                      <Input
                        value={draft.subject}
                        onChange={(ev) => updateDraft(e.enquiry_id, "subject", ev.target.value)}
                        data-testid="reply-subject"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Message</Label>
                      <Textarea
                        rows={8}
                        value={draft.body}
                        onChange={(ev) => updateDraft(e.enquiry_id, "body", ev.target.value)}
                        placeholder="Type your reply here…"
                        data-testid="reply-body"
                      />
                      <p className="text-[11px] text-[#9CA3AF] mt-1">
                        Your branded signature (logo, name, tel, email, website) is appended automatically.
                      </p>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        onClick={() => sendReply(e)}
                        disabled={draft.sending}
                        className="bg-[#37B160] hover:bg-[#2D9450] text-white"
                        data-testid="reply-send-btn"
                      >
                        <Reply className="h-4 w-4 mr-2" />
                        {draft.sending ? "Sending…" : "Send reply"}
                      </Button>
                    </div>
                  </div>

                  {/* Reply history */}
                  {replies.length > 0 && (
                    <div className="space-y-2" data-testid={`reply-history-${e.enquiry_id}`}>
                      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">Reply history ({replies.length})</div>
                      {replies.map((r, idx) => (
                        <details key={r.reply_id || idx} className="bg-white border border-black/5 rounded-lg p-3 group">
                          <summary className="cursor-pointer text-sm flex items-center justify-between">
                            <span className="font-medium truncate">{r.subject}</span>
                            <span className="text-[11px] text-[#9CA3AF] ml-3 whitespace-nowrap">
                              {new Date(r.sent_at).toLocaleString("en-GB")}
                            </span>
                          </summary>
                          <div className="mt-2 text-xs text-[#4B5563] border-t border-black/5 pt-2">
                            <div><strong>To:</strong> {r.to}</div>
                            <div><strong>Sent by:</strong> {r.sent_by}</div>
                            <div className="mt-2 whitespace-pre-wrap text-sm text-[#0A0F00]">{r.body}</div>
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-[#9CA3AF]">No enquiries yet.</div>}
      </div>
    </div>
  );
}
